import React from "react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import styles from "./WeekDateStrip.module.css";

export interface WeekDateStripProps {
  weekDates: Date[];
  selectedDate: Date;
  /** YYYY-MM-DD keys of days in this week that have at least one scheduled
   *  dose or conditional medicine — drives the "has data" visual state. */
  datesWithData: Set<string>;
  onSelect: (date: Date) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  prevWeekLabel: string;
  nextWeekLabel: string;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const WeekDateStrip: React.FC<WeekDateStripProps> = ({
  weekDates,
  selectedDate,
  datesWithData,
  onSelect,
  onPrevWeek,
  onNextWeek,
  prevWeekLabel,
  nextWeekLabel,
}) => {
  const today = new Date();
  return (
    // The prev/next buttons float as circles over the date row (see Figma's
    // "week-view" node) rather than sitting inline beside it — inline
    // buttons would consume their own flex space and visibly gap the strip
    // away from the rest of the header cluster.
    <div className={styles.weekView}>
      <div className={styles.dateRow}>
        {weekDates.map((date) => {
          // Today always renders filled, even if a different day is
          // selected — "selected" only gets its own (outlined) look when it
          // isn't today.
          const isToday = isSameDay(date, today);
          const isSelected = isSameDay(date, selectedDate);
          const hasData = datesWithData.has(toDateKey(date));
          const stateClass = isToday
            ? styles.today
            : isSelected
            ? styles.selectedOutline
            : hasData
            ? styles.hasData
            : styles.noData;
          return (
            <button
              type="button"
              key={date.toISOString()}
              className={`${styles.dateCard} ${stateClass}`}
              onClick={() => onSelect(date)}
            >
              <span className={styles.dayLabel}>
                {DAY_LABELS[date.getDay()]}
              </span>
              <span className={styles.dayNumber}>{date.getDate()}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={`${styles.navButton} ${styles.navButtonLeft}`}
        onClick={onPrevWeek}
        aria-label={prevWeekLabel}
      >
        <IconChevronLeft size={16} />
      </button>
      <button
        type="button"
        className={`${styles.navButton} ${styles.navButtonRight}`}
        onClick={onNextWeek}
        aria-label={nextWeekLabel}
      >
        <IconChevronRight size={16} />
      </button>
    </div>
  );
};

export default WeekDateStrip;
